import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "api/api";
import { Campaign, NewCampaign } from "model/campaign";
import { loginReset } from "redux/slice/authentication";
import { arrayToById, objectToById } from "redux/normalize";
import { CampaignResource } from "api/resource/campaign";

export interface CampaignsState {
	loading?: boolean;
	error?: string | null;
	byId: {
		[key: number]: CampaignResource;
	};
};

const initialState: CampaignsState = {
	byId: {},
};

const fetchCampaigns = createAsyncThunk("campaigns/fetchCampaigns", async (payload, thunkAPI) => {
	thunkAPI.dispatch(campaignsLoading());
	const result = api.getCampaigns();
	result
		.then((campaigns) => thunkAPI.dispatch(campaignsLoaded(campaigns)))
		.catch(error => thunkAPI.dispatch(campaignsFailed(error)));
	return result;
});

const createCampaign = createAsyncThunk("campaigns/createCampaign", async (payload: {campaign: NewCampaign}, thunkAPI) => {
	thunkAPI.dispatch(campaignsLoading());
	const result = api.createCampaign(payload.campaign);
	result
		.then(campaign => thunkAPI.dispatch(campaignLoaded(campaign)))
		.catch(error => thunkAPI.dispatch(campaignsFailed(error)));
	return result;
});

const updateCampaign = createAsyncThunk("campaigns/updateCampaign", async (payload: {campaign: Campaign}, thunkAPI) => {
	const previous = (thunkAPI.getState() as { campaigns: CampaignsState }).campaigns.byId[payload.campaign.id];
	// MAX-3901: optimistically merge the edit into the store so store-driven UI (e.g. the campaign
	// header name) updates immediately instead of briefly flashing the previously saved value while
	// the PUT round-trips. Rolls back to `previous` if the save fails. Overlaid fields mirror the
	// request body built in api.updateCampaign.
	if (previous) {
		thunkAPI.dispatch(campaignLoaded({
			...previous,
			name: payload.campaign.name,
			description: payload.campaign.description,
			campaignType: payload.campaign.campaignType ? payload.campaign.campaignType.id : null,
			marketId: payload.campaign.marketId,
			emailBrandingYn: payload.campaign.emailBrandingYn,
			// MAX-3878: include the new display category FK so a category move reflects in the
			// grouped view immediately instead of flashing the prior grouping until the PUT returns.
			campaignDisplayCategoryId: payload.campaign.campaignDisplayCategoryId,
		}));
	} else {
		thunkAPI.dispatch(campaignsLoading());
	}
	const result = api.updateCampaign(payload.campaign);
	result
		.then(campaign => thunkAPI.dispatch(campaignLoaded(campaign)))
		.catch(error => {
			if (previous) {
				thunkAPI.dispatch(campaignLoaded(previous));
			}
			thunkAPI.dispatch(campaignsFailed(error));
		});
	return result;
});

export const campaignSlice = createSlice({
  name: "campaigns",
  initialState,
  reducers: {
	campaignsLoading: (state) => {
		state.loading = true;
	},
	campaignsLoaded: (state, action: PayloadAction<CampaignResource[]>) => {
		state.loading = false;
		state.error = null;
		state.byId = arrayToById(action.payload, "id");
	},
	campaignsFailed: (state, action: PayloadAction<any>) => {
		state.loading = false;
		state.error = action.payload.message;
	},
	campaignLoaded: (state, action: PayloadAction<CampaignResource>) => {
		const byId = objectToById(action.payload, "id");
		state.loading = false;
		state.error = null;
		state.byId = { 
			...state.byId,
			...byId,
		};
	},
	default:() => {
		return;
	}
  }, 
  extraReducers(builder) {
	builder.addCase(loginReset, (state) => {
		return {
			...initialState,
			error: state.error,
		};
	});
  },
});

const { campaignsFailed, campaignsLoaded, campaignsLoading, campaignLoaded } = campaignSlice.actions;
export { fetchCampaigns, createCampaign, updateCampaign };